import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownLeftIcon,
  ArrowSquareOutIcon,
  ArrowUpRightIcon,
  ChartLineUpIcon,
  LifebuoyIcon,
  QuestionIcon,
  RankingIcon,
  ReceiptIcon,
  RobotIcon,
  StorefrontIcon,
  UserPlusIcon,
} from '@phosphor-icons/react/dist/ssr';

import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import {
  Card,
  CardContent,
  CardEyebrow,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { useT, type TFunction } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';

type AppRow = {
  to: string;
  labelKey: string;
  descKey: string;
  Icon: typeof StorefrontIcon;
};

const APP_ROWS: AppRow[] = [
  { to: '/markets', labelKey: 'help.app.markets_label', descKey: 'help.app.markets_desc', Icon: StorefrontIcon },
  { to: '/portfolio', labelKey: 'help.app.portfolio_label', descKey: 'help.app.portfolio_desc', Icon: ChartLineUpIcon },
  { to: '/orders', labelKey: 'help.app.orders_label', descKey: 'help.app.orders_desc', Icon: ReceiptIcon },
  { to: '/deposit', labelKey: 'help.app.deposit_label', descKey: 'help.app.deposit_desc', Icon: ArrowDownLeftIcon },
  { to: '/withdraw', labelKey: 'help.app.withdraw_label', descKey: 'help.app.withdraw_desc', Icon: ArrowUpRightIcon },
  { to: '/ranking', labelKey: 'help.app.ranking_label', descKey: 'help.app.ranking_desc', Icon: RankingIcon },
  { to: '/copy', labelKey: 'help.app.copy_label', descKey: 'help.app.copy_desc', Icon: UserPlusIcon },
];

const BOT_ROWS = [
  'help.bot.market',
  'help.bot.portfolio',
  'help.bot.wallet',
  'help.bot.deposit',
  'help.bot.withdraw',
  'help.bot.whale',
  'help.bot.copy',
  'help.bot.ranking',
  'help.bot.lang',
  'help.bot.help',
];

export default function Help() {
  const t = useT();
  useTelegramBackButton();
  useNavStackEntry({ label: t('help.breadcrumb') });

  const eyebrow = useMemo(
    () => (
      <span className="inline-flex items-center gap-1.5">
        <QuestionIcon weight="fill" className="size-3" />
        {t('help.eyebrow')}
      </span>
    ),
    [t],
  );

  return (
    <ScreenShell
      eyebrow={eyebrow}
      title={t('help.title')}
      description={t('help.description')}
    >
      <NavBar />

      <div className="flex flex-col gap-2">
        <CardEyebrow className="pl-1">
          <StorefrontIcon weight="fill" className="size-3" />
          {t('help.section_app')}
        </CardEyebrow>
        <div className="flex flex-col gap-2">
          {APP_ROWS.map((r) => (
            <AppRowCard key={r.to} row={r} t={t} />
          ))}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardEyebrow>
            <RobotIcon weight="fill" className="size-3" />
            {t('help.section_bot')}
          </CardEyebrow>
          <CardTitle className="text-base">/help</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col gap-1.5 text-[13px] leading-relaxed">
            {BOT_ROWS.map((key) => (
              <li
                key={key}
                className="text-muted-foreground"
                dangerouslySetInnerHTML={{
                  __html: t(key)
                    .replace(/`([^`]+)`/g, '<code class="font-mono text-foreground bg-foreground/5 px-1 py-0.5 rounded">$1</code>'),
                }}
              />
            ))}
          </ul>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-2">
        <CardEyebrow className="pl-1">
          <LifebuoyIcon weight="fill" className="size-3" />
          {t('help.section_links')}
        </CardEyebrow>
        <a
          href="https://polymarket.com"
          target="_blank"
          rel="noreferrer"
          className={cn(
            buttonVariants({ variant: 'outline', size: 'lg' }),
            'justify-between',
          )}
        >
          <span>{t('help.links.polymarket')}</span>
          <ArrowSquareOutIcon weight="bold" className="size-4" />
        </a>
        <Link
          to="/settings"
          className={cn(
            buttonVariants({ variant: 'outline', size: 'lg' }),
            'justify-between',
          )}
        >
          <span>{t('help.links.support')}</span>
          <ArrowSquareOutIcon weight="bold" className="size-4" />
        </Link>
      </div>
    </ScreenShell>
  );
}

function AppRowCard({ row, t }: { row: AppRow; t: TFunction }) {
  return (
    <Link
      to={row.to}
      className="group bg-card/90 ring-foreground/10 hover:ring-foreground/25 hover:bg-card flex items-center gap-3 rounded-2xl p-3 ring-1 backdrop-blur-md transition-all"
    >
      <span className="bg-primary/12 ring-primary/25 inline-flex size-9 items-center justify-center rounded-xl ring-1">
        <row.Icon weight="fill" className="text-foreground size-4" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-foreground text-[14px] font-semibold">
          {t(row.labelKey)}
        </span>
        <span className="text-muted-foreground text-[12px] leading-relaxed">
          {t(row.descKey)}
        </span>
      </div>
      <ArrowSquareOutIcon
        weight="bold"
        className="text-muted-foreground size-4 opacity-60 transition-opacity group-hover:opacity-100"
        aria-hidden
      />
    </Link>
  );
}
